import { zodResolver } from '@hookform/resolvers/zod';
import { requestPhotoUploadUrls, searchTickets } from '@jq4race/api-client';
import { type TFunction, translateApiError, useTranslation } from '@jq4race/i18n';
import type { CheckinFormConfigResponse, VisitorType } from '@jq4race/shared';
import { PHOTO_ANGLES, VISITOR_TYPES } from '@jq4race/shared';
import { Button, Input, Label, Tabs, TabsContent, TabsList, TabsTrigger } from '@jq4race/ui';
import { AlertTriangle, ScanLine, WifiOff } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { setFormDirty } from '../../../../app/dirty-form-registry';
import { env } from '../../../../app/env';
import { QrScanner } from '../../../../shared/ui/QrScanner';
import { enqueueCheckin } from '../../../offline/application/enqueue';
import { foregroundDrainer } from '../../../offline/application/foreground-drainer-instance';
import type { OutboxItem } from '../../../offline/domain/outbox-item';
import { generateUuidV4 } from '../../../offline/domain/uuid';
import { offlineDb } from '../../../offline/infrastructure/dexie/offline-db';
import { DexieBlobStore } from '../../../offline/infrastructure/dexie-blob-store';
import { DexieOutboxRepository } from '../../../offline/infrastructure/dexie-outbox.repository';
import { compressPhoto } from '../../../offline/infrastructure/image-compressor';
import { useStorageGuard } from '../../../offline/ui/use-storage-guard';
import { useOnlineStatus } from '../../../shell/hooks/use-online-status';
import { PageHeader } from '../../../shell/ui/PageHeader';
import {
  checkDuplicateActivePlate,
  findDuplicateInOutbox,
} from '../../application/check-duplicate-active-plate';
import { LocalIncidentPhotoSink } from '../../infrastructure/local-incident-photo-sink';
import { LocalPhotoSink } from '../../infrastructure/local-photo-sink';
import { CameraCapture } from '../components/CameraCapture';
import { CheckinSuccessScreen } from '../components/CheckinSuccessScreen';
import { DuplicatePlateDialog } from '../components/DuplicatePlateDialog';
import { IncidentPhotos } from '../components/IncidentPhotos';
import { MemberBanner } from '../components/MemberBanner';
import { PhotoCaptureGrid } from '../components/PhotoCaptureGrid';
import { useCheckinConfig } from '../hooks/use-checkin-config';
import { useMembershipLookup } from '../hooks/use-membership-lookup';
import { usePhotoUpload } from '../hooks/usePhotoUpload';

// pwa-offline-first Slice 6 (design §5) — one Dexie-backed outbox repo and
// blob store per app instance, shared by every CheckinPage mount (matches
// the `offlineKv` singleton pattern already used in `_authenticated.tsx`).
// Constructing a class instance is cheap (no IndexedDB connection is opened
// until a table is actually touched) so module scope is safe here, and
// keeps CheckinPage from re-creating them (and their captured `db`
// reference) on every render.
const outboxRepo = new DexieOutboxRepository(offlineDb);
const blobStore = new DexieBlobStore(offlineDb);

// ── E.164 phone — mirrors the shared schema constraint ────────────────────────
const E164 = /^\+[1-9]\d{6,14}$/;

// ── Dynamic zod schema — built from config after it loads ─────────────────────

function buildCheckinSchema(config: CheckinFormConfigResponse | null, t: TFunction<'checkin'>) {
  const niche = config?.niche?.niche ?? 'restaurant';
  const isHotel = niche === 'hotel';
  const fields = config?.checkinConfig?.fields ?? [];

  const fieldRequired = (id: string): boolean => fields.some((f) => f.id === id && f.required);

  const optionalOrRequired = (validator: z.ZodString, id: string) =>
    fieldRequired(id) ? validator.min(1, t('page.form.fieldRequired')) : validator.optional();

  return z.object({
    plate: z.string().trim().min(1, t('page.form.plateRequired')).max(20),

    // Ticket type — always present
    ticketType: z.enum(['sms', 'qr']),

    // Phone — optional but must be E.164 when provided
    phone: z
      .string()
      .optional()
      .refine((v) => !v || v.trim() === '' || E164.test(v), t('page.form.phoneInvalid')),

    // Keychain QR tag — valet-keychain-qr: always optional, independent of
    // ticketType (ticketType is retained only as a legacy analytics label —
    // see CreateTicketUseCase's decoupled gates). Scannable via the QrScanner
    // overlay or typed manually; server-side dup-active-tag rejection (422
    // qr_tag_conflict) is unaffected.
    qrTag: z.string().trim().max(64).optional(),

    // General fields (nicheOnly: null) — not gated on hotel
    firstName: optionalOrRequired(z.string().trim().max(60), 'first_name'),

    lastName: optionalOrRequired(z.string().trim().max(60), 'last_name'),

    visitorEmail: fieldRequired('visitor_email')
      ? z.string().trim().email(t('page.form.emailInvalid')).max(254)
      : z
          .string()
          .trim()
          .max(254)
          .optional()
          .refine(
            (v) => !v || v.trim() === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
            t('page.form.emailInvalid'),
          ),

    parkingZone: optionalOrRequired(z.string().trim().max(40), 'parking_zone'),

    valetNotes: optionalOrRequired(z.string().trim().max(500), 'valet_notes'),

    // Hotel-only fields (nicheOnly: 'hotel') — gated on isHotel
    roomNumber:
      isHotel && fieldRequired('room_number')
        ? z.string().trim().min(1, t('page.form.roomNumberRequired')).max(20)
        : z.string().optional(),

    visitorType: isHotel
      ? fieldRequired('visitor_type')
        ? z.enum(VISITOR_TYPES, { required_error: t('page.form.visitorTypeRequired') })
        : z.enum(VISITOR_TYPES).optional()
      : z.enum(VISITOR_TYPES).optional(),
  });
}

type CheckinFormValues = {
  plate: string;
  ticketType: 'sms' | 'qr';
  phone?: string;
  qrTag?: string;
  // General fields (nicheOnly: null)
  firstName?: string;
  lastName?: string;
  visitorEmail?: string;
  parkingZone?: string;
  valetNotes?: string;
  // Hotel-only fields (nicheOnly: 'hotel')
  roomNumber?: string;
  visitorType?: VisitorType;
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function deriveDefaultTicketType(config: CheckinFormConfigResponse | null): 'sms' | 'qr' {
  const smsEnabled = config?.ticketTypesConfig?.smsEnabled ?? true;
  const qrEnabled = config?.ticketTypesConfig?.qrEnabled ?? false;
  if (smsEnabled) return 'sms';
  if (qrEnabled) return 'qr';
  // Fallback: sms (until backend enables at least one)
  return 'sms';
}

function noop() {
  // intentionally empty
}

/**
 * pwa-offline-first Slice 1 (ticket-idempotent-creation) — verify-report
 * finding #1b. Canonical snapshot of every field the BACKEND fingerprint
 * (create-ticket-fingerprint.ts, Step 7b) compares, used to detect whether
 * this submission repeats the LAST one (same clientRequestId reused) or is a
 * corrected resubmission (new id — never reuse an id across two logically
 * different requests). Deliberately excludes photoKeys/incidentPhotoKeys
 * (per-attempt upload tokens) and `force` (never persisted on the ticket) —
 * mirrors the backend's exclusion list exactly. Pure function: same input →
 * same string, always.
 */
export function computeFormFingerprint(body: {
  plate: string;
  ticketType: string;
  phone?: string;
  qrTag?: string;
  firstName?: string;
  lastName?: string;
  visitorEmail?: string;
  parkingZone?: string;
  valetNotes?: string;
  roomNumber?: string;
  visitorType?: string;
}): string {
  return JSON.stringify({
    plate: body.plate,
    ticketType: body.ticketType,
    phone: body.phone ?? null,
    qrTag: body.qrTag ?? null,
    firstName: body.firstName ?? null,
    lastName: body.lastName ?? null,
    visitorEmail: body.visitorEmail ?? null,
    parkingZone: body.parkingZone ?? null,
    valetNotes: body.valetNotes ?? null,
    roomNumber: body.roomNumber ?? null,
    visitorType: body.visitorType ?? null,
  });
}

// ── Component ─────────────────────────────────────────────────────────────────

interface CheckinPageProps {
  locationId: string;
  /**
   * pwa-offline-first Slice 6 (design D12) — the org/operator that CAPTURED
   * this check-in, stamped onto the outbox row so a later sync can never
   * upload an item under a DIFFERENT org's session (guard enforced in
   * Slice 7; these fields exist from the first enqueue onward). Resolved by
   * the route wrapper (`_authenticated.checkin.tsx`) from the live session
   * or, in offline-shell mode, the cached offline profile.
   */
  orgId: string;
  capturedByUserId: string;
}

export function CheckinPage({ locationId, orgId, capturedByUserId }: CheckinPageProps) {
  const { t } = useTranslation('checkin');
  const isOnline = useOnlineStatus();
  // pwa-offline-first Slice 6 (design D17) — VITE_OFFLINE_CAPTURE=drain
  // keeps Slice 7's sync engine draining whatever is ALREADY queued but
  // refuses NEW offline captures: check-in falls back to requiring
  // connectivity, exactly like pre-Slice-6 behavior, ONLY while offline.
  // 'on' (the default) never blocks anything here.
  const offlineCaptureBlocked = !isOnline && env.offlineCapture === 'drain';
  const { config, loading: configLoading } = useCheckinConfig(locationId);
  // pwa-offline-first Slice 7, task 7.8 — "wire storage-guard.ts live".
  // `blocked` mirrors `MIN_FREE_BYTES_TO_CAPTURE` (15MB free): capturing a
  // new check-in (more compressed photo blobs) into an already-exhausted
  // IndexedDB would risk the write itself failing, silently losing the
  // capture. Fails open (false) whenever the Storage Manager API is
  // unavailable — see useStorageGuard's own doc comment.
  const storageGuard = useStorageGuard();

  // Ticket type toggle — controlled externally so the zod schema can react
  const [ticketType, setTicketType] = useState<'sms' | 'qr'>(() => deriveDefaultTicketType(config));

  // When config first arrives, align ticketType to what is actually enabled
  const smsEnabled = config?.ticketTypesConfig?.smsEnabled ?? true;
  const qrEnabled = config?.ticketTypesConfig?.qrEnabled ?? false;
  const bothEnabled = smsEnabled && qrEnabled;

  const form = useForm<CheckinFormValues>({
    resolver: zodResolver(buildCheckinSchema(config, t)),
    defaultValues: {
      plate: '',
      ticketType: 'sms',
      phone: '',
      qrTag: '',
      firstName: '',
      lastName: '',
      visitorEmail: '',
      parkingZone: '',
      valetNotes: '',
      roomNumber: '',
      visitorType: undefined,
    },
    mode: 'onSubmit',
  });

  const { errors, isSubmitting, isDirty } = form.formState;

  // ADR-017 / design D10 — a service-worker update must never auto-apply
  // (and reload the page) while the valet is mid check-in. See
  // dirty-form-registry.ts and app/service-worker.ts.
  useEffect(() => {
    setFormDirty(isDirty);
    return () => setFormDirty(false);
  }, [isDirty]);

  // pwa-offline-first Slice 1 (ticket-idempotent-creation) — one UUID per
  // "form session" (this check-in attempt-set). Every submitCreate() call
  // for the SAME session — including a force-retry after the duplicate-plate
  // dialog — reuses this id, so CreateTicketUseCase's idempotency check
  // recognizes a retry as the SAME logical request. A genuinely NEW check-in
  // (onNewCheckin, after success) gets a fresh id.
  //
  // verify-report finding #1b — a corrected resubmission (the valet edited
  // phone/room/etc. after a failed attempt) must NOT reuse the stale id: the
  // backend now correctly rejects that as 409 idempotency_key_reused (finding
  // #1a) rather than silently replaying stale data, but bouncing the valet
  // with an error is still worse UX than simply minting a fresh id for a
  // request that IS, in fact, new. `lastSubmittedFingerprintRef` remembers
  // the fingerprint of the LAST submitted attempt; a mismatch on the next
  // submit means the form content changed → generate a new id before
  // sending. `null` means "no submit yet" — the very first submit always
  // reuses the initial id (nothing to compare against).
  const [clientRequestId, setClientRequestId] = useState<string>(() => generateUuidV4());
  const lastSubmittedFingerprintRef = useRef<string | null>(null);

  // pwa-offline-first Slice 6 — LocalPhotoSink (task 4.4) needs the CURRENT
  // clientRequestId read FRESH on every upload() call (its own doc comment),
  // but the sink instance itself is created ONCE (usePhotoUpload's own
  // `sinkRef.current = sink ?? ...` never re-evaluates `sink` after mount).
  // A ref bridges the two: state drives re-renders, the ref is what the
  // sink actually reads.
  const clientRequestIdRef = useRef(clientRequestId);
  useEffect(() => {
    clientRequestIdRef.current = clientRequestId;
  }, [clientRequestId]);

  const localPhotoSinkRef = useRef<LocalPhotoSink | null>(null);
  if (!localPhotoSinkRef.current) {
    localPhotoSinkRef.current = new LocalPhotoSink(
      blobStore,
      compressPhoto,
      () => clientRequestIdRef.current,
    );
  }

  // Third adversarial review finding H3 — incident photos are durable LOCAL
  // blobs now too (never uploaded to R2 at capture time), same rationale as
  // localPhotoSinkRef above.
  const localIncidentPhotoSinkRef = useRef<LocalIncidentPhotoSink | null>(null);
  if (!localIncidentPhotoSinkRef.current) {
    localIncidentPhotoSinkRef.current = new LocalIncidentPhotoSink(
      blobStore,
      compressPhoto,
      () => clientRequestIdRef.current,
    );
  }

  const [enqueuedItem, setEnqueuedItem] = useState<OutboxItem | null>(null);
  // pwa-offline-first Slice 6 course correction — outbox-always removed the
  // synchronous createTicket() 409 that used to drive this dialog. Restored
  // as a best-effort PRE-enqueue check (checkDuplicateActivePlate) so the
  // capability itself never silently disappears — see that module's own
  // doc comment for the full rationale.
  const [duplicate, setDuplicate] = useState<{ label: string } | null>(null);
  const [topError, setTopError] = useState<string | null>(null);
  // valet-keychain-qr — scan-to-fill state for the always-visible qrTag field
  const [scanningQrTag, setScanningQrTag] = useState(false);

  // ── Membership coverage lookup (frequent-customer-memberships S4) ─────────
  // Advisory only — fires on plate blur, drives the "MEMBER — do not charge"
  // banner. The server-authoritative decision is enforced independently by
  // CreateTicketUseCase at submit time.
  const {
    result: membershipResult,
    lookup: lookupMembership,
    lookupDebounced: lookupMembershipDebounced,
    reset: resetMembershipLookup,
  } = useMembershipLookup();
  const registeredPlate = form.register('plate');

  // ── Photo capture (ADR-22-5: component state, not RHF; D2 — extracted into
  // usePhotoUpload so single check-in and Flash's angle grid share one
  // source of truth) ─────────────────────────────────────────────────────
  const {
    slots: photoSlots,
    allPhotosDone,
    activeAngle: activeCameraAngle,
    onSlotCapture: handleSlotCapture,
    onSlotRetake: handleSlotRetake,
    onCameraCapture: handleCameraCapture,
    onCameraCancel: handleCameraCancel,
    photoKeys: hookPhotoKeys,
    reset: resetPhotoUpload,
  } = usePhotoUpload({
    angles: PHOTO_ANGLES,
    requestUrls: requestPhotoUploadUrls,
    // pwa-offline-first Slice 6 (design D2) — single check-in now uploads
    // to local IndexedDB, not R2, at capture time. Flash is UNCHANGED: its
    // own usePhotoUpload call passes no `sink`, so it still defaults to
    // RemotePhotoSink.
    sink: localPhotoSinkRef.current,
  });

  // ── Incident photos (ticket-incident-photos, Slice 2) ─────────────────────
  // Optional, angle-less, bounded 0..20 — fully independent of the mandatory
  // 4-angle gate above. Owned/stored internally by <IncidentPhotos>; this
  // page only tracks the resulting identifiers for enqueueCheckin.
  //
  // Third adversarial review finding H3 — these are now LOCAL blob ids
  // (`item.incidentPhotoRefs`), uploaded fresh at SYNC time by the handler
  // — never server storage keys carried through `payload` from capture
  // time (those were tokens with a short, 15-min TTL; a late or
  // different-operator sync could land after the token was purged,
  // silently losing damage evidence with no recovery path).
  const [incidentPhotoRefs, setIncidentPhotoRefs] = useState<string[]>([]);
  const handleIncidentKeysChange = useCallback((refs: string[]) => {
    setIncidentPhotoRefs(refs);
  }, []);

  const niche = config?.niche?.niche ?? 'restaurant';
  const isHotel = niche === 'hotel';
  const configFields = config?.checkinConfig?.fields ?? [];
  const fieldRequired = (id: string) => configFields.some((f) => f.id === id && f.required);
  const fieldVisible = (id: string) => configFields.some((f) => f.id === id);

  // pwa-offline-first Slice 6 (design D5/D12, decision #12) — outbox-always:
  // submit NEVER calls createTicket() directly and NEVER blocks on
  // `isOnline` or the 4 photo uploads. It durably enqueues the check-in
  // (photos are already durably stored locally by LocalPhotoSink at capture
  // time) and returns immediately with a provisional confirmation. Most of
  // what used to be a synchronous error response — qr_tag_conflict,
  // idempotency_key_reused, photo_token_invalid, required fields,
  // ticket_type_disabled — moves to SYNC time and is handled per-item by
  // Slice 7's RejectedItemSheet/sync-error-policy, not inline on this form.
  // Duplicate plate is the ONE exception (course correction, see
  // check-duplicate-active-plate.ts): its operator-facing warn-and-force
  // capability is restored as a best-effort PRE-enqueue check below, since
  // losing it silently would be a real regression, not merely a deferred
  // UX improvement. The only OTHER failure this catch block can still see
  // is a LOCAL enqueue failure (e.g. IndexedDB quota/eviction) — everything
  // else becomes an outbox item state, observed live on the success screen
  // (CheckinSuccessScreen, task 6.7).
  async function submitCreate(values: CheckinFormValues, force?: boolean) {
    // Client gate: all 4 photos required (SC-1) — unchanged, still enforced
    // even offline (spec "4 mandatory photos").
    if (!allPhotosDone) return;
    // design D17 kill switch — see offlineCaptureBlocked's own doc comment.
    if (offlineCaptureBlocked) return;
    // MEDIUM fix (adversarial review) — "re-check on submit, not only on
    // mount". The mount-time `storageGuard.blocked` can go stale over a
    // long-lived session (more photos captured since mount); refresh()
    // re-reads the estimate right now and returns the fresh result
    // directly, so this gate always acts on the CURRENT answer.
    const freshStorageGuard = await storageGuard.refresh();
    if (freshStorageGuard.blocked) return;

    setTopError(null);

    // pwa-offline-first Slice 6 course correction — a best-effort, ADVISORY
    // pre-enqueue duplicate-plate check (see check-duplicate-active-plate.ts
    // for the full rationale), skipped only when already confirmed via the
    // dialog's Force action.
    //
    // coordinator finding #3 — the LOCAL outbox check runs FIRST and
    // UNCONDITIONALLY (pure Dexie read, no network — works identically
    // online or offline): a plate already queued on THIS device, still
    // unsynced, is just as real a duplicate as one already on the server,
    // and it is the ONLY duplicate an offline device can ever know about.
    // The SERVER check only runs when online, on top of that — exactly
    // matching "offline → check local only". A lookup FAILURE (timeout,
    // 5xx) on the server check is swallowed the same way
    // `useMembershipLookup` already swallows its own — a network hiccup on
    // an advisory check must never block a check-in.
    if (!force) {
      const unsyncedItems = await outboxRepo.unsynced();
      const localMatch = findDuplicateInOutbox(values.plate, orgId, locationId, unsyncedItems);
      if (localMatch) {
        setDuplicate({ label: localMatch.provisionalCode });
        return;
      }

      if (isOnline) {
        try {
          const existing = await checkDuplicateActivePlate(values.plate, searchTickets);
          if (existing) {
            setDuplicate({ label: `#${existing.existingTicketNumber}` });
            return;
          }
        } catch {
          // Advisory only — fall through and enqueue anyway.
        }
      }
    }

    try {
      const photoKeys = hookPhotoKeys;

      // pwa-offline-first Slice 1 — fingerprint fields ONLY (excludes
      // force/photoKeys/incidentPhotoKeys/clientRequestId, matching the
      // backend's create-ticket-fingerprint.ts exclusion list exactly).
      const fingerprintFields = {
        plate: values.plate.trim().toUpperCase(),
        ticketType,
        phone: values.phone?.trim() || undefined,
        qrTag: values.qrTag?.trim() || undefined,
        firstName: values.firstName?.trim() || undefined,
        lastName: values.lastName?.trim() || undefined,
        visitorEmail: values.visitorEmail?.trim() || undefined,
        parkingZone: values.parkingZone?.trim() || undefined,
        valetNotes: values.valetNotes?.trim() || undefined,
        roomNumber: isHotel ? values.roomNumber?.trim() || undefined : undefined,
        visitorType: isHotel ? values.visitorType || undefined : undefined,
      };

      // verify-report finding #1b — reuse the SAME clientRequestId only for
      // a byte-identical retry (force-retry from the duplicate-plate
      // dialog, or a plain resubmit after a local failure, both send the
      // UNCHANGED form values). The moment the fingerprint differs from the
      // last submitted attempt, this is a genuinely NEW request — mint a
      // fresh id so a later sync never mistakes it for the previous one.
      const currentFingerprint = computeFormFingerprint(fingerprintFields);
      let requestId = clientRequestId;
      if (
        lastSubmittedFingerprintRef.current !== null &&
        lastSubmittedFingerprintRef.current !== currentFingerprint
      ) {
        requestId = generateUuidV4();
        setClientRequestId(requestId);
        clientRequestIdRef.current = requestId;
      }
      lastSubmittedFingerprintRef.current = currentFingerprint;

      const payload = {
        ...fingerprintFields,
        photoKeys,
        // third adversarial review finding H3 — incidentPhotoKeys is NO
        // LONGER set here: incident photos are local blob ids
        // (`incidentPhotoRefs`, passed to enqueueCheckin below) uploaded
        // fresh at sync time, exactly like the 4 mandatory angles
        // (`photoKeys` above is likewise just a placeholder the handler
        // always overwrites at sync — see checkin-create.handler.ts).
        // coordinator finding #2 — the operator's explicit "force anyway"
        // decision (duplicate-plate dialog) must reach the backend's own
        // duplicate check at sync time, same as the pre-Slice-6 behavior.
        // Deliberately NOT part of `fingerprintFields`/the idempotency
        // fingerprint (matches create-ticket-fingerprint.ts's own exclusion
        // list — forcing a retry must never be mistaken for a content change).
        force: force ?? false,
      };

      const item = await enqueueCheckin(
        outboxRepo,
        {
          clientRequestId: requestId,
          orgId,
          capturedByUserId,
          locationId,
          payload,
          photoRefs: photoKeys,
          incidentPhotoRefs,
        },
        // second adversarial review BLOCKER #1 — re-links these blobs to
        // `requestId` when it differs from the id they were captured under
        // (a failed-enqueue-then-edit minted a NEW id above).
        blobStore,
        // fourth adversarial review, LOW #4 — makes the put()+relink atomic
        // (single Dexie transaction across outbox+blobs).
        offlineDb,
      );

      resetPhotoUpload();
      setIncidentPhotoRefs([]);
      setDuplicate(null);
      setEnqueuedItem(item);
      resetMembershipLookup();

      // coordinator finding #1 — outbox-always still means an enqueued item
      // needs an actual network trip to ever leave "provisional". Kick the
      // minimal foreground drainer right after a successful enqueue, fire-
      // and-forget (never awaited — must not block this UI). Only when
      // online: offline, the drainer has nothing it could reach anyway, and
      // the boot/online/periodic triggers (useForegroundDrainerTriggers,
      // mounted in _authenticated.tsx) pick it up once connectivity returns.
      if (isOnline) {
        void foregroundDrainer.tick({ orgId, capturedByUserId });
      }
    } catch (err: unknown) {
      setTopError(translateApiError(err, t, { fallback: t('page.errors.generic') }));
    }
  }

  // valet-keychain-qr D7 — ticketType is now a legacy analytics label only;
  // it no longer gates which fields are visible/required, so switching it no
  // longer clears the phone/qrTag fields (both are always-optional and
  // independent of the toggle).
  function handleTicketTypeChange(next: string) {
    const value = next as 'sms' | 'qr';
    setTicketType(value);
    form.setValue('ticketType', value);
    form.clearErrors();
  }

  if (enqueuedItem) {
    return (
      <CheckinSuccessScreen
        outboxId={enqueuedItem.id}
        provisionalCode={enqueuedItem.provisionalCode}
        plate={enqueuedItem.payload.plate as string}
        onNewCheckin={() => {
          setEnqueuedItem(null);
          form.reset();
          setTicketType(deriveDefaultTicketType(config));
          resetPhotoUpload();
          resetMembershipLookup();
          // pwa-offline-first Slice 1 — a genuinely NEW check-in gets a fresh
          // idempotency key; the previous one stays bound to the ticket that
          // was just created. Reset the fingerprint memory too, so the
          // fingerprint-change check doesn't compare this brand-new form
          // against the just-completed one's leftover values.
          const nextId = generateUuidV4();
          setClientRequestId(nextId);
          clientRequestIdRef.current = nextId;
          lastSubmittedFingerprintRef.current = null;
        }}
      />
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={t('page.title')}
        description={t('page.description')}
      />

      <section className="space-y-5">
        {/* Offline banner — informational only (design D5/D12): check-in is
            NEVER blocked offline, it is queued locally and synced later.
            EXCEPT under the design D17 `drain` kill switch, which shows a
            blocking message instead (matches the disabled submit button). */}
        {!isOnline && (
          <div
            role="alert"
            className="flex items-center gap-2 rounded-sm border border-destructive/60 bg-destructive/10 px-3 py-3 text-[13px] text-foreground"
          >
            <WifiOff className="size-4 shrink-0 text-destructive" strokeWidth={1.5} />
            <span>
              {offlineCaptureBlocked ? t('page.offlineCaptureDisabled') : t('page.offline')}
            </span>
          </div>
        )}

        {/* pwa-offline-first Slice 7, task 7.8 — storage-guard wired live.
            Blocked takes priority over the plain 80% warning (both cannot
            usefully show at once). */}
        {storageGuard.blocked && (
          <div
            role="alert"
            className="flex items-center gap-2 rounded-sm border border-destructive/60 bg-destructive/10 px-3 py-3 text-[13px] text-foreground"
          >
            <AlertTriangle className="size-4 shrink-0 text-destructive" strokeWidth={1.5} />
            <span>{t('page.storageBlocked')}</span>
          </div>
        )}
        {!storageGuard.blocked && storageGuard.warnUsage && (
          <div className="flex items-center gap-2 rounded-sm border border-amber-500/60 bg-amber-500/10 px-3 py-3 text-[13px] text-foreground">
            <AlertTriangle className="size-4 shrink-0 text-amber-600" strokeWidth={1.5} />
            <span>{t('page.storageWarning')}</span>
          </div>
        )}

        {/* Config loading */}
        {configLoading && (
          <p className="text-[13px] text-muted-foreground">{t('page.loadingConfig')}</p>
        )}

        {/* Top-level error — pwa-offline-first Slice 6: only a LOCAL enqueue
            failure (e.g. IndexedDB quota) reaches this now. Every OTHER
            backend rejection (qr_tag_conflict, required fields,
            ticket_type_disabled, photo_token_invalid, etc. — everything
            EXCEPT duplicate plate, which keeps its own pre-enqueue check
            below) now surfaces at sync time via Slice 7's
            RejectedItemSheet, not here. */}
        {topError && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-sm border border-destructive/60 bg-destructive/10 px-3 py-3 text-[13px] text-foreground"
          >
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />
            <span>{topError}</span>
          </div>
        )}

        {/* Duplicate plate — pwa-offline-first Slice 6 course correction:
            a best-effort PRE-enqueue check (see
            check-duplicate-active-plate.ts) restores this capability now
            that submit no longer gets a synchronous 409 from the backend. */}
        {duplicate && (
          <DuplicatePlateDialog
            existingLabel={duplicate.label}
            onConfirm={() => void form.handleSubmit((values) => submitCreate(values, true))()}
            onCancel={() => setDuplicate(null)}
          />
        )}

        <form
          className="space-y-5"
          onSubmit={form.handleSubmit((values) => void submitCreate(values))}
          noValidate
        >
          {/* ── Plate ── */}
          <div className="grid gap-2">
            <Label htmlFor="plate">{t('page.form.plateLabel')}</Label>
            <Input
              id="plate"
              type="text"
              autoComplete="off"
              autoCapitalize="characters"
              placeholder={t('page.form.platePlaceholder')}
              aria-invalid={!!errors.plate}
              className="h-14 text-[16px] font-semibold tracking-widest uppercase"
              {...registeredPlate}
              onChange={(e) => {
                void registeredPlate.onChange(e);
                // Debounced lookup as the valet types — the banner appears without
                // needing a blur (mobile keyboards often submit without blurring).
                // A stale banner is cleared immediately inside lookupDebounced.
                lookupMembershipDebounced(e.target.value);
              }}
              onBlur={(e) => {
                void registeredPlate.onBlur(e);
                // Immediate lookup on blur too (belt-and-suspenders).
                void lookupMembership(e.target.value);
              }}
            />
            {errors.plate && <p className="text-[13px] text-destructive">{errors.plate.message}</p>}
            {membershipResult?.isCovered && (
              <MemberBanner memberName={membershipResult.memberName} />
            )}
          </div>

          {/* ── Ticket type toggle (both enabled) ── */}
          {bothEnabled && (
            <div className="grid gap-2">
              <Label>{t('page.form.ticketTypeLabel')}</Label>
              <Tabs value={ticketType} onValueChange={handleTicketTypeChange}>
                <TabsList className="w-full rounded-sm border border-border bg-secondary p-1">
                  <TabsTrigger value="sms" className="flex-1 h-12 text-[14px]">
                    SMS
                  </TabsTrigger>
                  <TabsTrigger value="qr" className="flex-1 h-12 text-[14px]">
                    QR
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="sms" />
                <TabsContent value="qr" />
              </Tabs>
            </div>
          )}

          {/* ── SMS: phone — valet-keychain-qr D7: "SMS now universal", shown
               whenever the location enables it, independent of the ticketType
               toggle (SMS dispatch is now gated on phone presence, not type). ── */}
          {smsEnabled && (
            <div className="grid gap-2">
              <Label htmlFor="phone">
                {t('page.form.phoneLabel')}{' '}
                <span className="font-normal normal-case text-muted-foreground">
                  {t('page.form.phoneHint')}
                </span>
              </Label>
              <Input
                id="phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder={t('page.form.phonePlaceholder')}
                aria-invalid={!!errors.phone}
                className="h-12"
                {...form.register('phone')}
              />
              {errors.phone && (
                <p className="text-[13px] text-destructive">{errors.phone.message}</p>
              )}
            </div>
          )}

          {/* ── Keychain QR tag — valet-keychain-qr D7: ALWAYS visible as an
               optional field with a scan button, independent of ticketType/mode.
               The manual text input remains fully usable whether or not the
               scanner is available (SC — scanner permission denial handled
               gracefully / manual fallback). ── */}
          <div className="grid gap-2">
            <Label htmlFor="qrTag">{t('page.form.qrTagLabel')}</Label>
            {scanningQrTag ? (
              <QrScanner
                label={t('page.form.qrTagLabel')}
                onDecode={(text) => {
                  form.setValue('qrTag', text, { shouldValidate: true });
                  setScanningQrTag(false);
                }}
                onCancel={() => setScanningQrTag(false)}
              />
            ) : (
              <div className="flex gap-2">
                <Input
                  id="qrTag"
                  type="text"
                  autoComplete="off"
                  placeholder={t('page.form.qrTagPlaceholder')}
                  aria-invalid={!!errors.qrTag}
                  className="h-12 flex-1"
                  {...form.register('qrTag')}
                />
                <Button
                  type="button"
                  variant="ghost"
                  className="h-12 px-3"
                  onClick={() => setScanningQrTag(true)}
                  aria-label={t('page.form.scanQrTagAria')}
                >
                  <ScanLine className="size-4" />
                  {t('page.form.scanQrTag')}
                </Button>
              </div>
            )}
            {errors.qrTag && <p className="text-[13px] text-destructive">{errors.qrTag.message}</p>}
          </div>

          {/* ── General configurable fields (nicheOnly: null) ── */}

          {/* First name */}
          {fieldVisible('first_name') && (
            <div className="grid gap-2">
              <Label htmlFor="firstName">
                {fieldRequired('first_name')
                  ? t('page.form.firstNameLabel')
                  : t('page.form.firstNameLabelOptional')}
              </Label>
              <Input
                id="firstName"
                type="text"
                autoComplete="given-name"
                placeholder={t('page.form.firstNamePlaceholder')}
                aria-invalid={!!errors.firstName}
                className="h-12"
                {...form.register('firstName')}
              />
              {errors.firstName && (
                <p className="text-[13px] text-destructive">{errors.firstName.message}</p>
              )}
            </div>
          )}

          {/* Last name */}
          {fieldVisible('last_name') && (
            <div className="grid gap-2">
              <Label htmlFor="lastName">
                {fieldRequired('last_name')
                  ? t('page.form.lastNameLabel')
                  : t('page.form.lastNameLabelOptional')}
              </Label>
              <Input
                id="lastName"
                type="text"
                autoComplete="family-name"
                placeholder={t('page.form.lastNamePlaceholder')}
                aria-invalid={!!errors.lastName}
                className="h-12"
                {...form.register('lastName')}
              />
              {errors.lastName && (
                <p className="text-[13px] text-destructive">{errors.lastName.message}</p>
              )}
            </div>
          )}

          {/* Visitor email */}
          {fieldVisible('visitor_email') && (
            <div className="grid gap-2">
              <Label htmlFor="visitorEmail">
                {fieldRequired('visitor_email')
                  ? t('page.form.emailLabel')
                  : t('page.form.emailLabelOptional')}
              </Label>
              <Input
                id="visitorEmail"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder={t('page.form.emailPlaceholder')}
                aria-invalid={!!errors.visitorEmail}
                className="h-12"
                {...form.register('visitorEmail')}
              />
              {errors.visitorEmail && (
                <p className="text-[13px] text-destructive">{errors.visitorEmail.message}</p>
              )}
            </div>
          )}

          {/* Parking zone */}
          {fieldVisible('parking_zone') && (
            <div className="grid gap-2">
              <Label htmlFor="parkingZone">
                {fieldRequired('parking_zone')
                  ? t('page.form.parkingZoneLabel')
                  : t('page.form.parkingZoneLabelOptional')}
              </Label>
              <Input
                id="parkingZone"
                type="text"
                autoComplete="off"
                placeholder={t('page.form.parkingZonePlaceholder')}
                aria-invalid={!!errors.parkingZone}
                className="h-12"
                {...form.register('parkingZone')}
              />
              {errors.parkingZone && (
                <p className="text-[13px] text-destructive">{errors.parkingZone.message}</p>
              )}
            </div>
          )}

          {/* Valet notes */}
          {fieldVisible('valet_notes') && (
            <div className="grid gap-2">
              <Label htmlFor="valetNotes">
                {fieldRequired('valet_notes')
                  ? t('page.form.notesLabel')
                  : t('page.form.notesLabelOptional')}
              </Label>
              <textarea
                id="valetNotes"
                rows={3}
                autoComplete="off"
                placeholder={t('page.form.notesPlaceholder')}
                aria-invalid={!!errors.valetNotes}
                className="flex w-full rounded-sm border border-input bg-popover px-3 py-2 font-sans text-[15px] text-foreground transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                {...form.register('valetNotes')}
              />
              {errors.valetNotes && (
                <p className="text-[13px] text-destructive">{errors.valetNotes.message}</p>
              )}
            </div>
          )}

          {/* ── Hotel-only fields (nicheOnly: 'hotel') ── */}
          {isHotel && (
            <>
              {/* Room number */}
              {fieldVisible('room_number') && (
                <div className="grid gap-2">
                  <Label htmlFor="roomNumber">
                    {fieldRequired('room_number')
                      ? t('page.form.roomNumberLabel')
                      : t('page.form.roomNumberLabelOptional')}
                  </Label>
                  <Input
                    id="roomNumber"
                    type="text"
                    autoComplete="off"
                    placeholder={t('page.form.roomNumberPlaceholder')}
                    aria-invalid={!!errors.roomNumber}
                    className="h-12"
                    {...form.register('roomNumber')}
                  />
                  {errors.roomNumber && (
                    <p className="text-[13px] text-destructive">{errors.roomNumber.message}</p>
                  )}
                </div>
              )}

              {/* Visitor type */}
              {fieldVisible('visitor_type') && (
                <div className="grid gap-2">
                  <Label htmlFor="visitorType">
                    {fieldRequired('visitor_type')
                      ? t('page.form.visitorTypeLabel')
                      : t('page.form.visitorTypeLabelOptional')}
                  </Label>
                  <select
                    id="visitorType"
                    aria-invalid={!!errors.visitorType}
                    className="flex h-12 w-full rounded-sm border border-input bg-popover px-3 py-2 font-sans text-[15px] text-foreground transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50"
                    {...form.register('visitorType')}
                  >
                    <option value="">{t('page.form.visitorTypePlaceholder')}</option>
                    {VISITOR_TYPES.map((vt) => (
                      <option key={vt} value={vt}>
                        {vt.charAt(0).toUpperCase() + vt.slice(1)}
                      </option>
                    ))}
                  </select>
                  {errors.visitorType && (
                    <p className="text-[13px] text-destructive">{errors.visitorType.message}</p>
                  )}
                </div>
              )}
            </>
          )}

          {/* ── Vehicle photos (JQ4-22 / SC-1, SC-3, SC-6) ── */}
          <section className="space-y-3" aria-label={t('page.vehiclePhotos.title')}>
            <div>
              <p className="jq-body-sm font-semibold text-foreground">
                {t('page.vehiclePhotos.title')}
              </p>
              <p className="jq-caption text-muted-foreground">
                {t('page.vehiclePhotos.description')}
              </p>
            </div>

            {/* Inline camera — shown when a slot is being captured */}
            {activeCameraAngle ? (
              <div className="space-y-2">
                <p className="jq-caption text-muted-foreground">
                  {t('page.vehiclePhotos.capturing')}{' '}
                  <span className="font-medium capitalize text-foreground">
                    {activeCameraAngle}
                  </span>
                </p>
                <CameraCapture
                  angle={activeCameraAngle}
                  onCapture={(blob) => void handleCameraCapture(blob)}
                  onCancel={handleCameraCancel}
                />
              </div>
            ) : (
              <PhotoCaptureGrid
                slots={photoSlots}
                onSlotCapture={handleSlotCapture}
                onSlotRetake={handleSlotRetake}
                // allPhotosDone is now derived inside usePhotoUpload from the
                // same `slots` — this prop is a no-op here (kept because
                // PhotoCaptureGrid is shared with Flash's angle grid).
                onAllDoneChange={noop}
              />
            )}
          </section>

          {/* ── Incident photos (ticket-incident-photos) — optional, does NOT gate submit ──
              pwa-offline-first Slice 7 (deferred finding from Slice 6 round 5, HIGH
              #1(a)) — hidden when the org's plan does not include damageReports, so
              the operator never captures evidence the backend will just drop at
              sync time. `!== false` keeps it SHOWN when the field is absent (an
              older cached config, pre-Slice-7) — the sync-time non-fatal-drop
              handling (Slice 6) remains the backstop for a plan downgraded between
              capture and sync. */}
          {config?.damageReportsEnabled !== false && (
            <IncidentPhotos
              onKeysChange={handleIncidentKeysChange}
              sink={localIncidentPhotoSinkRef.current}
            />
          )}

          {/* ── Submit ── */}
          <Button
            type="submit"
            size="touch"
            // pwa-offline-first Slice 6 (design D5/D12) — never gated on
            // `isOnline` alone: outbox-always means submit is always a
            // local, durable enqueue, never a network call. The ONE
            // exception is the design D17 `drain` kill switch, which
            // deliberately reinstates the pre-Slice-6 offline block.
            disabled={
              isSubmitting || !allPhotosDone || offlineCaptureBlocked || storageGuard.blocked
            }
            className="h-14 w-full text-[15px]"
          >
            {isSubmitting ? t('page.submitting') : t('page.submit')}
          </Button>

          {/* ── Photo gate hint (SC-1) ── */}
          {!allPhotosDone && !activeCameraAngle && (
            <p role="note" className="text-center text-[13px] text-muted-foreground">
              {t('page.photoGateHint')}
            </p>
          )}
        </form>
      </section>
    </div>
  );
}
